import { nanoid } from "nanoid";
import create from "zustand";

import type { AskSource } from "@performyard/service-contracts";

import type { DocumentFile } from "../../pages/Documents/schema";

import type { ChatMessage, ChatStatus, ConfidenceLevel, StreamStage } from "./types";

const GREETING_MESSAGE: ChatMessage = {
	id: "greeting",
	role: "assistant",
	content:
		"Hi there! Have a question? I'll search your company's internal documents to help you find the most accurate and relevant information.",
	timestamp: new Date(),
};

type DocumentModalState = {
	isOpen: boolean;
	document: DocumentFile | null;
};

type AskPerformYardState = {
	// UI State
	isOpen: boolean;
	isExpanded: boolean;

	// Chat State
	sessionId: string;
	messages: ChatMessage[];
	status: ChatStatus;
	streamStage: StreamStage | null;
	error: string | null;
	inputValue: string;

	// Feature State
	hasNoDocuments: boolean;

	// Document Modal State
	documentModal: DocumentModalState;

	// Scroll State
	scrollToBottom: (() => void) | null;
	setScrollToBottom: (fn: (() => void) | null) => void;

	// Actions
	open: () => void;
	close: () => void;
	toggleExpand: () => void;
	setInputValue: (value: string) => void;

	// Message Actions
	addUserMessage: (content: string) => string;
	startAssistantMessage: () => string;
	appendToAssistantMessage: (messageId: string, chunk: string) => void;
	completeAssistantMessage: (
		messageId: string,
		sources?: AskSource[],
		confidence?: ConfidenceLevel,
		isIncomplete?: boolean
	) => void;

	// Status Actions
	setStatus: (status: ChatStatus) => void;
	setStreamStage: (stage: StreamStage | null) => void;
	setError: (error: string | null) => void;
	setHasNoDocuments: (hasNoDocuments: boolean) => void;

	// Document Modal Actions
	openDocumentModal: (s3FileId: string, filename: string) => void;
	closeDocumentModal: () => void;

	// Reset (note: does not reset UI state like isOpen/isExpanded)
	resetChat: () => void;
};

const generateMessageId = (): string => {
	return `msg-${nanoid()}`;
};

// Session ID helpers — persisted in sessionStorage so it survives full page reloads
// (e.g. React ↔ Template page transitions) within the same tab.
const SESSION_ID_KEY = "ask-py-session-id";

const getOrCreateSessionId = (): string => {
	const existing = sessionStorage.getItem(SESSION_ID_KEY);
	if (existing) return existing;
	const id = nanoid();
	sessionStorage.setItem(SESSION_ID_KEY, id);
	return id;
};

const resetSessionId = (): string => {
	const id = nanoid();
	sessionStorage.setItem(SESSION_ID_KEY, id);
	return id;
};

const initialDocumentModalState: DocumentModalState = {
	isOpen: false,
	document: null,
};

// Streaming chunk buffer — accumulates chunks and flushes once per animation frame
// to avoid triggering a full re-render on every tiny chunk
let pendingChunks = "";
let pendingMessageId: string | null = null;
let rafHandle: number | null = null;

const flushPendingChunks = (): void => {
	rafHandle = null;
	const chunks = pendingChunks;
	const messageId = pendingMessageId;
	pendingChunks = "";
	pendingMessageId = null;

	if (!messageId || !chunks) return;

	useAskPerformYardStore.setState((state) => {
		const lastIdx = state.messages.length - 1;
		const lastMsg = state.messages[lastIdx];
		if (!lastMsg || lastMsg.id !== messageId) return state;

		const updated = state.messages.slice();
		updated[lastIdx] = { ...lastMsg, content: lastMsg.content + chunks };
		return { messages: updated };
	});
};

const drainPendingChunks = (): string => {
	if (rafHandle !== null) {
		cancelAnimationFrame(rafHandle);
		rafHandle = null;
	}
	const chunks = pendingChunks;
	pendingChunks = "";
	pendingMessageId = null;
	return chunks;
};

export const useAskPerformYardStore = create<AskPerformYardState>((set) => ({
	// Initial UI State
	isOpen: false,
	isExpanded: false,

	// Initial Chat State
	sessionId: getOrCreateSessionId(),
	messages: [GREETING_MESSAGE],
	status: "idle",
	streamStage: null,
	error: null,
	inputValue: "",

	// Initial Feature State
	hasNoDocuments: false,

	// Initial Document Modal State
	documentModal: initialDocumentModalState,

	// Initial Scroll State
	scrollToBottom: null,
	setScrollToBottom: (fn: (() => void) | null) => set({ scrollToBottom: fn }),

	// UI Actions
	open: () => set({ isOpen: true }),
	close: () => set({ isOpen: false }),
	toggleExpand: () => set((state) => ({ isExpanded: !state.isExpanded })),
	setInputValue: (value: string) => set({ inputValue: value }),

	// Message Actions
	addUserMessage: (content: string) => {
		const id = generateMessageId();
		set((state) => ({
			messages: [
				...state.messages,
				{
					id,
					role: "user",
					content,
					timestamp: new Date(),
				},
			],
			inputValue: "",
		}));
		return id;
	},

	startAssistantMessage: () => {
		const id = generateMessageId();
		set((state) => ({
			messages: [
				...state.messages,
				{
					id,
					role: "assistant",
					content: "",
					isStreaming: true,
					timestamp: new Date(),
				},
			],
			status: "streaming",
		}));
		return id;
	},

	appendToAssistantMessage: (messageId: string, chunk: string) => {
		pendingMessageId = messageId;
		pendingChunks += chunk;
		if (rafHandle === null) {
			rafHandle = requestAnimationFrame(flushPendingChunks);
		}
	},

	completeAssistantMessage: (
		messageId: string,
		sources?: AskSource[],
		confidence?: ConfidenceLevel,
		isIncomplete?: boolean
	) => {
		const remainingChunks = drainPendingChunks();

		set((state) => {
			const lastIdx = state.messages.length - 1;
			const lastMsg = state.messages[lastIdx];
			if (!lastMsg || lastMsg.id !== messageId) return state;

			const updated = state.messages.slice();
			updated[lastIdx] = {
				...lastMsg,
				content: lastMsg.content + remainingChunks,
				isStreaming: false,
				sources,
				confidence,
				isIncomplete,
			};
			return { messages: updated, status: "idle" as const, streamStage: null };
		});
	},

	// Status Actions
	setStatus: (status: ChatStatus) => set({ status }),
	setStreamStage: (stage: StreamStage | null) => set({ streamStage: stage }),
	setError: (error: string | null) =>
		set((state) => ({
			error,
			status: error ? "error" : state.status === "error" ? "idle" : state.status,
		})),
	setHasNoDocuments: (hasNoDocuments: boolean) => set({ hasNoDocuments }),

	// Document Modal Actions
	openDocumentModal: (s3FileId: string, filename: string) => {
		set({
			documentModal: {
				isOpen: true,
				document: {
					id: s3FileId,
					_id: s3FileId,
					filename,
					// Minimal required fields - the modal uses id/filename for iframe preview
					clean: true,
					company: "",
					length: 0,
					owner: "",
					tags: [],
					uploader: "",
					uploadedAt: "",
				},
			},
		});
	},
	closeDocumentModal: () => set({ documentModal: initialDocumentModalState }),

	// Reset chat state (note: does not reset UI state like isOpen/isExpanded)
	resetChat: () => {
		drainPendingChunks();
		set({
			sessionId: resetSessionId(),
			messages: [GREETING_MESSAGE],
			status: "idle",
			streamStage: null,
			error: null,
			inputValue: "",
			hasNoDocuments: false,
			documentModal: initialDocumentModalState,
		});
	},
}));
